<template lang="pug">
    .play-box
        template(v-if='!isLoading')
                game-header(
                    v-if="!isBattle"
                    @soundStateChanged ="headerKey = !headerKey"
                    :key="headerKey"
                    :slug='type',
                    :game-type='parameters && parameters.courseType',
                    :is-homework="!!exerciseId",
                )
                .play-box__game(v-if='gameStarted && !gameFinished')
                    .play-box__example
                        .play-box__overflow-container
                            .play-box__field
                                .play-box__letter-container(
                                        v-for="(object, index) in wholeWordForCurrentExample"
                                        :class="{'stressed': isAddStressedClass(object.userColor)}"
                                        :style="object.userColor.value"
                                        @click="insertPossibleAnswer(index)"
                                    )
                                    | {{ object.letter }}
                            .play-box__word-transcription(v-if="showTranscription")
                                | {{ transcriptionWordForCurrentExample }}
                            cs-button.play-box__next-example-button(
                                    v-if="!isCorrectAnswerGiven",
                                    @click="processReply()",
                                    pattern="primary-outline",
                                    color="blue"
                                )  {{ $i18n.t('trainer.trainer_gamePlayBox_checkAnswer') }}
                            cs-button.play-box__next-example-button(
                                    v-if="isCorrectAnswerGiven",
                                    @click="nextExample",
                                    pattern="primary-outline",
                                    color="blue"
                                )  {{ $i18n.t('olympiads.olympiads_processStage_further') }}
                            .play-box__colors
                                .color_container(
                                    v-for="color in possibleColors"
                                    :class=`{
                                        active: color.color === currentUserAnswer.color, 
                                        stressed: isAddStressedClass(color)
                                    }`
                                    :style="color.value"
                                    @click="currentUserAnswer = color"
                                )
                .game-info__block(v-if="gameStarted && !gameFinished")
                        game-info(
                            :score="{left: answers.good, right: answers.bad}"
                            :time="getTime"
                            :slug="type"
                            :parameters="parameters"
                            :game-allows-tips="true"
                            :opponent="opponent"
                            :opponent-numbers="opponentNumbers"
                            :is-opponent-answering="isOpponentAnswering"
                            :is-from-battle="isBattle"
                            :is-olympiad="isOlympiad"
                        )
                .play-box__end-container(v-if='gameFinished && !isOlympiad')
                    fireworks(
                        v-if="gameFinished && !isOlympiad && gameResult === 'success' "
                        :is-mobile="isMobile"
                    )
                    .play-box__reaction-container
                            .play-box__answers.play-box__answers--left
                                | {{ $i18n.t('trainer.trainer_game_wordField_yourTime') }}
                                br
                                | {{ getTime }}
                            result-image.play-box__image(:status="gameResult")
                            .play-box__answers.play-box__answers--right
                                | {{ $i18n.t('dashboard.dashboard_sidebar_statistics') }}
                                br
                                | {{ answers.good }} / {{ answers.bad }}
                    result-buttons(
                        v-if="!isBattle"
                        :exercise-id="exerciseId"
                        :games-type="parameters.courseType"
                        show-restart-button
                        @restartGame="restartGame"
                    )
                        slot
                calculating-answers(v-if="calculatingAnswers" :is-last-game-in-battle="isLastGameInBattle")
        cs-loader(v-else)
</template>

<script>
    /*eslint-disable*/
    import hardSoftConsonantsSchoolPreparing from '@/api/games/hardSoftConsonantsSchoolPreparing';
    import textScrollMixin from '../../../../utils/trainer/textScroll';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import Battle from '../../../../utils/battle/battleMixin';
    import olympiadRussianLanguageAnswerTimerMixin from '../../../../utils/olympiads/olympiadRussianLanguageAnswerTimerMixin.js';
    import { cloneDeep } from 'lodash';
    import CsButton from '@/elements/CsButton.vue';
    import CsLoader from '@/elements/CsLoader.vue';
    import CalculatingAnswers from '../../CalculatingAnswersModal.vue';

    const DEFAULT_VOLUME = 0.6;

    const DEFAULT_OBJECT_COLOR = {
        color: [],
        value: { 'background-color': 'white' },
        description: '',
    };

    const STRESSED_COLORS = new Set(['ку', 'зку']);

    function DEFAULT_DATA() {
        return {
            examples: null,
            clientWidth: 0,
            currentLevel: 0,
            answers: { good: 0, bad: 0 },
            isCorrectAnswerGiven: false,
            isWrongAnswerGiven: false,
            userAnswer: null,
            currentUserAnswer: DEFAULT_OBJECT_COLOR,
            wholeWordForCurrentExample: null,
            transcriptionWordForCurrentExample: null,
            possibleColors: [
                {
                    color: ['з'],
                    value: { 'background-color': 'hsl(121, 38%, 58%)' },
                    description: 'мягкие согласные',
                },
                {
                    color: ['с'],
                    value: { 'background-color': 'hsl(211, 82%, 35%)' },
                    description: 'твердые согласные',
                },
                {
                    color: ['к'],
                    value: { 'background-color': 'hsl(14, 91%, 56%)' },
                    description: 'гласные',
                },
                {
                    color: ['ку'],
                    value: { 'background-color': 'hsl(14, 91%, 56%)' },
                    description: 'гласные ударные',
                },
                {
                    color: ['б'],
                    value: { 'background-color': 'hsl(0, 0%, 88%)' },
                    description: 'буквы, не содержащие звук',
                },
                {
                    color: ['зк'],
                    value: {
                        'background-image': 'linear-gradient( 45deg,hsl(121, 38%, 58%) 50%,hsl(14, 91%, 56%) 50%)',
                    },
                    description: 'йотированные буквы',
                },
                {
                    color: ['зку'],
                    value: {
                        'background-image': 'linear-gradient( 45deg,hsl(121, 38%, 58%) 50%,hsl(14, 91%, 56%) 50%)',
                    },
                    description: 'йотированные буквы',
                },
            ],
        };
    }

    export default {
        mixins: [textScrollMixin, playBoxGameMixin, olympiadRussianLanguageAnswerTimerMixin, Battle],
        created() {
            window.addEventListener('resize', this.getClientWidth);
        },
        data() {
            return {
                STRESSED_COLORS,
                ...DEFAULT_DATA(),
            };
        },
        mounted() {
            document.addEventListener('keydown', this.handleKeyPress);
        },
        computed: {
            showTranscription() {
                return this.isCorrectAnswerGiven && this.parameters.isTranscriptionEnabled;
            },
            gameResult() {
                if (this.answers.good >= this.answers.bad) return 'success';
                else return 'failure';
            },
            isInfiniteAmountOfExamples() {
                return this.parameters.timeLimit || this.parameters.infinityAmountOfExamples ? 1 : 0;
            },
        },
        methods: {
            handleKeyPress(event) {
                if (event.key === 'Enter') {
                    if (!this.isCorrectAnswerGiven && !this.gameFinished) {
                        return this.processReply();
                    };
                    if (this.isCorrectAnswerGiven && !this.gameFinished) {
                        return this.nextExample();
                    };
                }
            },
            processReply(finishExample = false) {
                this.wholeWordForCurrentExample.forEach(obj => {
                    if (obj.disabled) return;
                    if (obj.userColor.color.includes(obj.color)) {
                        this.answers.good++;
                        obj.disabled = true;
                    } else {
                        this.answers.bad++;
                        obj.userColor = DEFAULT_OBJECT_COLOR;
                    }
                });
                if (this.wholeWordForCurrentExample.every(({ disabled }) => disabled)) {
                    for (const object of this.wholeWordForCurrentExample) {
                        if (['ку', 'зку'].includes(object.color)) {
                            object.letter = object.letter.toUpperCase();
                            break;
                        }
                    }
                    this.isCorrectAnswerGiven = true;
                }
                if (this.isOlympiad || this.isBattle || finishExample) {
                    this.nextExample();
                }
            },
            nextExample() {
                this.stopAnswerTimer();
                if (!this.isInfiniteAmountOfExamples && this.currentLevel === this.parameters.amountOfExamples - 1) {
                    this.finishGame();
                    return;
                }
                this.currentLevel++;
                this.wholeWordForCurrentExample = this.getWholeWordForCurrentExample();
                this.transcriptionWordForCurrentExample = this.getTranscriptionForCurrentExample();
                this.isCorrectAnswerGiven = false;
                this.startAnswerTimer();
            },
            insertPossibleAnswer(index) {
                if (this.isCorrectAnswerGiven) return;
                if (!this.currentUserAnswer) return;
                this.wholeWordForCurrentExample[index].userColor = this.currentUserAnswer;
            },
            registerSounds() {
                this.$createjs.Sound.removeAllSounds();
                this.$createjs.Sound.removeAllEventListeners();
                this.$createjs.Sound.alternateExtensions = ['mp3', 'ogg'];
                this.$createjs.Sound.registerSounds(this.soundObjects);
            },
            fetchData() {
                this.getExercise()
                    .then(() => {
                        hardSoftConsonantsSchoolPreparing.getItemsByRequest({
                            isInfiniteAmountOfExamples: this.isInfiniteAmountOfExamples,
                            amountOfExamples: this.parameters.amountOfExamples,
                            theme: this.parameters.theme,
                        })
                            .then(data => {
                                this.examples = data;
                                this.startGame();
                                this.registerSounds();
                                this.isLoading = false;
                            })
                            .catch(e => this.showErrorMessage(e));
                    })
                    .catch(e => {
                        this.isLoading = false;
                        this.$toasted.error(this.$root.$t('errors.getting_level'));
                        this.$router.push({ name: 'dashboard' });
                    });
            },
            refreshGameStatBeforeSend() {
                this.gameStat.answers = this.answers.good;
                this.gameStat.amountOfMistakes = this.answers.bad;
                this.gameStat.attempts = this.parameters.amountOfExamples;
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = true;
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.amountOfExamples = this.parameters.amountOfExamples;
                this.gameStat.parameters = cloneDeep(this.parameters);
            },
            startGame() {
                this.gameStarted = true;
                this.wholeWordForCurrentExample = this.getWholeWordForCurrentExample();
                this.transcriptionWordForCurrentExample = this.getTranscriptionForCurrentExample();
                this.startTimer();
            },
            getTranscriptionForCurrentExample() {
                return this.examples[this.currentLevel].transcriptionWord;
            },
            getWholeWordForCurrentExample() {
                const result = [];
                let item = null;
                const brackets = new Set([')', '(']);
                for (const letter of this.examples[this.currentLevel].parsedWord.split('')) {
                    if (!item) {
                        item = {
                            letter,
                            color: '',
                            disabled: false,
                            userColor: {
                                color: [],
                                value: { 'background-color': 'white' },
                                description: '',
                            },
                        };
                        result.push(item);
                    } else {
                        if (brackets.has(letter)) {
                            if (letter === ')') {
                                item = null;
                            }
                            continue;
                        }
                        item.color += letter;
                    }
                }
                return result;
            },
            startTimer() {
                this.startAnswerTimer();
                this.$nextTick(() => {
                    if (!this.timer) {
                        this.setTimer();
                    }
                });
            },
            finishGame() {
                this.gameFinished = true;
                this.stopTimer();
                this.soundInstance = this.$createjs.Sound.play('gameDone');
                this.sendStat();
            },
            calculatePercent() {
                let total = this.answers.good + this.answers.bad;
                if (total > 0) {
                    return parseInt((this.answers.good / total) * 100);
                }
                return 0;
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
            isAddStressedClass(item) {
                return item.color.some(color => STRESSED_COLORS.has(color));
            },
        },
        beforeDestroy() {
            document.removeEventListener('keydown', this.handleKeyPress);
            window.removeEventListener('resize', this.getClientWidth);
        },
        components: {
            CsLoader,
            CsButton,
            CalculatingAnswers,
        },
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/sass/games/playbox.scss';

    .play-box {
        display: flex;
        flex: 1 1 auto;
        justify-content: center;
        width: 100%;
        align-items: center;
        flex-direction: column-reverse;
        @include media($md) {
            align-items: stretch;
            flex-direction: row;
        }
    }

    .mobile-version {
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    .play-box--warning {
        display: table;
    }

    .play-box__game {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column-reverse;
        @include media($lg) {
            flex-direction: row;
        }
    }

    .play-box__example {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-grow: 5;
        flex-direction: column;
    }

    .play-box__overflow-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    .play-box__words-row {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    .play-box__answer-container {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .answer-container__header {
        margin-bottom: 20px;
        font-weight: 500;
        font-size: 32px;
        line-height: 38px;
    }

    .answer-container__input .cs-input {
        margin-bottom: 0;
        font-size: 52px;
        line-height: 62px;
        padding: 9px 15px;
        text-align: center;
        max-width: 330px;
    }

    .play-box__word {
        padding: 0 8px;
        margin: 0 10px 3px 0;
        cursor: pointer;
    }

    .play-box__word:last-child {
        margin-right: 0;
    }

    .play-box__word--selected {
        color: $x-color-white;
        background: $hsl-color-green-58;
    }

    .play-box__word--unselected {
        color: $x-color-white;
        background: $hsl-color-red-62;
    }

    .play-box__word--inverted {
        display: inline-block;
        transform: rotate(180deg);
        margin-right: 10px;
    }

    .play-box__header {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        font-weight: 500;
        font-size: 28px;
        line-height: 34px;
        margin-bottom: 20px;
    }

    .play-box__before-start-game-header {
        flex-direction: column;
    }

    .play-box__task {
        align-self: center;
    }

    .play-box__title {
        margin-bottom: 20px;
        text-align: center;
    }

    .play-box__scaling-bar-block {
        margin-left: 20px;
        @include media($xlg) {
            margin-left: 40px;
        }
    }

    .play-box__subtitle {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 0;
        justify-content: center;
    }

    .play-box__end-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .play-box__reaction-container {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        width: 100%;
        @include media($md) {
            flex-direction: row;
        }
    }

    .play-box__answers {
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        text-align: center;
        @include media($lg) {
            font-size: 32px;
            line-height: 38px;
        }
        @include media($xl) {
            font-size: 42px;
            line-height: 50px;
        }
    }

    .play-box__answers--left {
        color: $hsl-color-green-58;
        margin-left: 20px;
        @include media($lg) {
            margin-left: 35px;
        }
    }

    .play-box__answers--right {
        color: $hsl-color-green-58;
        margin-right: 20px;
        @include media($lg) {
            margin-right: 35px;
        }
    }

    .play-box__answers--show-md {
        display: block;
    }

    .play-box__answers-container {
        display: flex;
    }

    .play-box__image {
        max-width: 100%;
        margin-bottom: 40px;
        @media screen and (max-width: 1200px) {
            height: 400px;
        }
    }

    .play-box__buttons {
        display: flex;
        flex-direction: column;
        margin-top: 25px;
        @include media($lg) {
            flex-direction: row;
        }
    }

    .play-box__buttons--game {
        margin: 0 0 25px;
        justify-content: center;
    }

    .play-box__button {
        margin: 20px 0;
        @include media($lg) {
            margin: 0 20px;
        }
    }

    .play-box__button:first-child {
        margin-top: 0;
        @include media($lg) {
            margin-left: 0;
        }
    }

    .play-box__button:last-child {
        margin-bottom: 0;
        @include media($lg) {
            margin-right: 0;
        }
    }

    .play-box__game-info {
        align-self: flex-end;
        @include media($lg) {
            align-self: auto;
        }
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }

    .play-box__answers-field {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
    }

    .play-box__answer {
        margin: 10px;
        padding: 15px;
        border: 3px solid $hsl-color-gray-74;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .play-box__next-example-button {
        margin-top: 15px;
    }

    .divide-it-right__answer-buttom {
        display: flex;
        flex-direction: column;
        align-items: center;
        @include inset-space($space-base);
    }

    .divide-it-right__answer-turtle {
        max-width: 180px;
        @include stack-space($space-base);
        @include media($xlg) {
            max-width: 240px;
        }
    }

    .stressed {
        &::before {
            position: absolute;
            top: -0.25em;
            content: ' ́';
            left: 50%;
            transform: translateX(25%);
            color: black;
            font-size: 2em;
            width: 100%;
        }
    }

    .play-box__field {
        padding: 0 20px;
        text-align: center;
        font-size: 18px;
        user-select: none;
        font-weight: bold;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.3em;
        font-size: 23px;
        @include media($sm) {
            font-size: 35px;
        }
        @include media($md) {
            font-size: 38px;
        }
        @include media($lg) {
            font-size: 49px;
        }
        @include media($xlg) {
            font-size: 66px;
        }
        @include media($xl) {
            font-size: 80px;
        }
    }

    .play-box__letter-container {
        position: relative;
        cursor: pointer;
        font-weight: bold;
        color: transparent;
        text-transform: uppercase;
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-stroke: 1px black;
        &.stressed {
            &::before {
                transform: translateX(0);
                font-size: 1.5em;
                top: -0.2em;
            }
        }
    }

    .play-box__colors {
        margin-top: 0.8em;
        display: grid;
        gap: 30px 15px;
        grid-template-columns: repeat(4, 1fr);
        font-size: 50px;
        @include media($md) {
            gap: 30px;
            grid-template-columns: repeat(4, 1fr);
        }
        @include media($lg) {
            gap: 35px;
            font-size: 55px;
            grid-template-columns: repeat(7, 1fr);
        }
        @include media($xlg) {
            font-size: 80px;
        }
    }

    .color_container {
        position: relative;
        cursor: pointer;
        min-width: 50px;
        height: 50px;
        border: 2px solid black;
        @include media($lg) {
            min-width: 55px;
            height: 55px;
        }
        @include media($xlg) {
            min-width: 80px;
            height: 80px;
        }
        &.active {
            box-shadow: 1px 1px 6px black;
        }
    }

    .play-box__word-transcription {
        font-size: 30px;
        margin-right: 15px;
        @include media($sm) {
            font-size: 35px;
        }
        @include media($md) {
            font-size: 45px;
        }
        @include media($lg) {
            font-size: 49px;
        }
        @include media($xlg) {
            font-size: 60px;
        }
    }
</style>
